import { useState, useEffect } from 'react';
import { Target, Activity, ClipboardCheck, Trophy, BookOpen } from '../icons';
import { LoadingSpinner } from '../LoadingSpinner';
import {
  fetchGoalsForUser, fetchHabitsForUser, fetchTasksForUser,
  fetchCheckInsForUser, fetchMilestonesForUser, fetchJournalEntriesForUser,
  type Goal, type Habit, type Task, type CheckIn, type Milestone, type JournalEntry,
} from '../../lib/progress';

export function CoachProgressView({ userId }: { userId: string }) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [habits, setHabits] = useState<Habit[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [journals, setJournals] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchGoalsForUser(userId),
      fetchHabitsForUser(userId),
      fetchTasksForUser(userId),
      fetchCheckInsForUser(userId, 30),
      fetchMilestonesForUser(userId),
      fetchJournalEntriesForUser(userId, 5),
    ]).then(([g, h, t, c, m, j]) => {
      setGoals(g);
      setHabits(h);
      setTasks(t);
      setCheckIns(c);
      setMilestones(m);
      setJournals(j);
    }).finally(() => setIsLoading(false));
  }, [userId]);

  if (isLoading) return <LoadingSpinner message="Loading progress..." />;

  const activeGoals = goals.filter(g => g.status === 'active');
  const openTasks = tasks.filter(t => t.status !== 'done');
  const recentCheckIn = checkIns[0];

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Goals', value: activeGoals.length, icon: Target, color: 'bg-sage-100 text-sage-600' },
          { label: 'Active Habits', value: habits.length, icon: Activity, color: 'bg-coral-100 text-coral-600' },
          { label: 'Open Tasks', value: openTasks.length, icon: ClipboardCheck, color: 'bg-amber-100 text-amber-600' },
          { label: 'Milestones', value: milestones.length, icon: Trophy, color: 'bg-yellow-100 text-yellow-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="freud-card-elevated p-4">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-chocolate-900">{value}</p>
            <p className="text-xs text-chocolate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Check-in history */}
      {checkIns.length > 0 && (
        <div className="freud-card-elevated p-6">
          <h3 className="freud-heading-lg text-chocolate-900 mb-4">Check-in History (last 30)</h3>
          <div className="mb-4 flex items-end gap-1 h-16">
            {checkIns.slice().reverse().map((c, i) => {
              const avg = (c.energy_level + c.mood_level + c.focus_level) / 3;
              const pct = Math.round((avg / 10) * 100);
              return (
                <div
                  key={i}
                  className="flex-1 bg-sage-400 rounded-t-sm transition-all"
                  style={{ height: `${pct}%` }}
                  title={`${new Date(c.checked_in_at).toLocaleDateString()} — avg ${avg.toFixed(1)}/10`}
                />
              );
            })}
          </div>
          {recentCheckIn && (
            <>
              <p className="text-xs text-chocolate-400 mb-3">
                Latest: {new Date(recentCheckIn.checked_in_at).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Energy', value: recentCheckIn.energy_level, color: 'bg-amber-400' },
                  { label: 'Mood', value: recentCheckIn.mood_level, color: 'bg-coral-400' },
                  { label: 'Focus', value: recentCheckIn.focus_level, color: 'bg-sage-400' },
                ].map(({ label, value, color }) => (
                  <div key={label}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-chocolate-500">{label}</span>
                      <span className="text-xs font-bold text-chocolate-700">{value}/10</span>
                    </div>
                    <div className="h-2 bg-cream-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${value * 10}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              {recentCheckIn.notes && <p className="mt-3 text-sm text-chocolate-500 italic">"{recentCheckIn.notes}"</p>}
            </>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Goals */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-sage-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Goals</h3>
          </div>
          {goals.length === 0 ? (
            <p className="text-sm text-chocolate-400">No goals set yet.</p>
          ) : (
            <div className="space-y-2">
              {goals.slice(0, 5).map(g => (
                <div key={g.id} className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    g.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                    g.status === 'active' ? 'bg-sage-100 text-sage-700' :
                    'bg-cream-200 text-chocolate-500'
                  }`}>{g.status}</span>
                  <span className="text-sm text-chocolate-700 truncate">{g.title}</span>
                  {g.target_date && <span className="text-[10px] text-chocolate-400 ml-auto flex-shrink-0">{new Date(g.target_date).toLocaleDateString()}</span>}
                </div>
              ))}
              {goals.length > 5 && <p className="text-xs text-chocolate-400">+{goals.length - 5} more</p>}
            </div>
          )}
        </div>

        {/* Habits */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-coral-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Habits</h3>
          </div>
          {habits.length === 0 ? (
            <p className="text-sm text-chocolate-400">No habits tracked yet.</p>
          ) : (
            <div className="space-y-2">
              {habits.slice(0, 5).map(h => {
                const thisWeek = (h.completions || []).length;
                const pct = Math.min((thisWeek / h.target_days_per_week) * 100, 100);
                return (
                  <div key={h.id}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-chocolate-700 truncate">{h.title}</span>
                      <span className="text-xs text-chocolate-400">{thisWeek}/{h.target_days_per_week}d</span>
                    </div>
                    <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
                      <div className="h-full bg-coral-400 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardCheck className="w-4 h-4 text-amber-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Tasks</h3>
          </div>
          {tasks.length === 0 ? (
            <p className="text-sm text-chocolate-400">No tasks yet.</p>
          ) : (
            <div className="space-y-2">
              {tasks.filter(t => t.status !== 'done').slice(0, 5).map(t => (
                <div key={t.id} className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    t.status === 'in_progress' ? 'bg-amber-100 text-amber-700' : 'bg-cream-200 text-chocolate-500'
                  }`}>{t.status === 'in_progress' ? 'In Progress' : 'To Do'}</span>
                  <span className="text-sm text-chocolate-700 truncate">{t.title}</span>
                </div>
              ))}
              <p className="text-xs text-chocolate-400">{tasks.filter(t => t.status === 'done').length} tasks completed</p>
            </div>
          )}
        </div>

        {/* Milestones */}
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-yellow-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Milestones</h3>
          </div>
          {milestones.length === 0 ? (
            <p className="text-sm text-chocolate-400">No milestones yet.</p>
          ) : (
            <div className="space-y-2">
              {milestones.slice(0, 5).map(m => (
                <div key={m.id} className="flex items-start gap-2">
                  <Trophy className="w-3.5 h-3.5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-chocolate-700">{m.title}</p>
                    <p className="text-[11px] text-chocolate-400">{new Date(m.achieved_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Journal entries */}
      {journals.length > 0 && (
        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-chocolate-600" />
            <h3 className="freud-heading-lg text-chocolate-900">Recent Journal Entries</h3>
          </div>
          <div className="space-y-3">
            {journals.map(j => (
              <div key={j.id} className="border-l-2 border-cream-300 pl-4">
                <div className="flex items-center justify-between mb-0.5">
                  <p className="text-sm font-medium text-chocolate-800">{j.title}</p>
                  <p className="text-[11px] text-chocolate-400">{new Date(j.created_at).toLocaleDateString()}</p>
                </div>
                <p className="text-xs text-chocolate-500 line-clamp-2">{j.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
